/**
 * Floating "Talk to a program advisor" CTA — anchors to bottom-right of the
 * viewport and expands into a compact 5-field booking form. Submissions land
 * in `pop_advisor_bookings` and appear in the super-admin dashboard.
 *
 * Also exports a <CohortCountdown /> tile — a live 4-column D·H·M·S timer.
 *
 * Both components are self-contained (no shell / no context deps) so they can
 * be dropped into any PoP page without extra wiring.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Phone, Send, X, CheckCircle2, Loader2, Calendar, Clock, MessageCircle,
} from 'lucide-react';
import { popCreateAdvisorBooking } from '../../lib/popApi';

// ============================================================================
// AdvisorFAB — floating action button + panel
// ============================================================================
export const AdvisorFAB = ({ program = 'pop', defaults = {} }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({
    name: defaults.name || '',
    email: defaults.email || '',
    phone: defaults.phone || '',
    role: defaults.role || 'industry_expert',
    slot: 'next-business-day',
    notes: '',
  });
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open]);

  const upd = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setErr(''); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(''); setDone(false);
    try {
      await popCreateAdvisorBooking({ ...form, program });
      setDone(true);
      setTimeout(() => { setOpen(false); setDone(false); }, 2500);
    } catch (ex) {
      // Pydantic 422 returns detail as an array of {loc, msg, type} — never render raw.
      const d = ex && ex.response && ex.response.data && ex.response.data.detail;
      let msg = 'Could not schedule — please retry';
      if (typeof d === 'string') msg = d;
      else if (Array.isArray(d) && d.length > 0 && typeof d[0]?.msg === 'string') {
        msg = d.map((x) => x.msg).slice(0, 3).join(' · ');
      } else if (ex && typeof ex.message === 'string') {
        msg = ex.message;
      }
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  const valid = form.name.trim().length >= 2
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
    && /^\+?[\d\s\-().]{6,20}$/.test(form.phone.trim());

  return (
    <div ref={wrapRef} className="fixed z-40 bottom-5 right-5 sm:bottom-8 sm:right-8">
      {open && (
        <div
          data-testid="advisor-fab-panel"
          className="mb-3 w-[22rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-amber-300/25 bg-slate-950/95 backdrop-blur-md shadow-2xl overflow-hidden"
        >
          <div className="px-5 py-4 bg-gradient-to-br from-amber-300/15 to-transparent border-b border-white/10 flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-300/15 border border-amber-300/40 flex items-center justify-center flex-shrink-0">
              <Phone className="w-4 h-4 text-amber-300" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300">TALK TO US</div>
              <div className="text-sm font-serif font-semibold text-white leading-tight mt-0.5">
                20-minute program advisor call
              </div>
              <div className="text-[11px] text-white/55 mt-1 leading-relaxed">
                Free · No commercials · Get cohort dates, curriculum fit and next steps.
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white/85 -mt-1 -mr-1" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {done ? (
            <div className="p-6 text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/20 border border-emerald-400/50 flex items-center justify-center mb-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-300" />
              </div>
              <div className="text-sm font-serif font-semibold text-white mb-1">We&apos;ll be in touch shortly.</div>
              <div className="text-[11px] text-white/60">A SAMANVAI advisor will reach out on your preferred slot.</div>
            </div>
          ) : (
            <form onSubmit={submit} className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-[9px] uppercase tracking-wider font-semibold text-white/50 mb-1">Your name *</label>
                <input required type="text" value={form.name} onChange={upd('name')} data-testid="advisor-name"
                  placeholder="Full name"
                  className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-amber-300/60 focus:outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[9px] uppercase tracking-wider font-semibold text-white/50 mb-1">Email *</label>
                  <input required type="email" value={form.email} onChange={upd('email')} data-testid="advisor-email"
                    placeholder="you@org.com"
                    className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-amber-300/60 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-[9px] uppercase tracking-wider font-semibold text-white/50 mb-1">Phone *</label>
                  <input required type="tel" value={form.phone} onChange={upd('phone')} data-testid="advisor-phone"
                    placeholder="+91 …"
                    className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-amber-300/60 focus:outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[9px] uppercase tracking-wider font-semibold text-white/50 mb-1">I am a…</label>
                  <select value={form.role} onChange={upd('role')} data-testid="advisor-role"
                    className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white focus:border-amber-300/60 focus:outline-none">
                    <option className="bg-slate-900" value="industry_expert">Industry expert</option>
                    <option className="bg-slate-900" value="university_director">University / Dean</option>
                    <option className="bg-slate-900" value="faculty">Faculty</option>
                    <option className="bg-slate-900" value="student">Working professional</option>
                    <option className="bg-slate-900" value="corporate">Corporate L&D</option>
                    <option className="bg-slate-900" value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[9px] uppercase tracking-wider font-semibold text-white/50 mb-1">Preferred slot</label>
                  <select value={form.slot} onChange={upd('slot')} data-testid="advisor-slot"
                    className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white focus:border-amber-300/60 focus:outline-none">
                    <option className="bg-slate-900" value="next-business-day">Next business day</option>
                    <option className="bg-slate-900" value="this-week">This week</option>
                    <option className="bg-slate-900" value="next-week">Next week</option>
                    <option className="bg-slate-900" value="weekend">Weekend</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[9px] uppercase tracking-wider font-semibold text-white/50 mb-1">Anything specific? (optional)</label>
                <textarea value={form.notes} onChange={upd('notes')} data-testid="advisor-notes" rows={2} maxLength={400}
                  placeholder="Cohort dates? Curriculum fit? University tie-up?"
                  className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-amber-300/60 focus:outline-none resize-none" />
              </div>

              {err && <div className="text-[11px] text-rose-300" data-testid="advisor-error">{err}</div>}

              <button type="submit" disabled={!valid || busy} data-testid="advisor-submit"
                className={`w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold shadow-md transition ${
                  valid && !busy
                    ? 'bg-amber-300 hover:bg-amber-200 text-slate-900'
                    : 'bg-white/10 text-white/40 cursor-not-allowed'
                }`}>
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Scheduling…</> : <><Send className="w-3.5 h-3.5" /> Request my call</>}
              </button>
              <div className="text-[10px] text-white/40 text-center">
                We&apos;ll never share your details. SAMANVAI-only outreach.
              </div>
            </form>
          )}
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="advisor-fab-toggle"
        aria-label="Talk to a program advisor"
        className="group inline-flex items-center gap-2 pl-3 pr-4 py-3 rounded-full bg-amber-300 hover:bg-amber-200 text-slate-900 shadow-2xl shadow-amber-300/20 border border-amber-200 font-semibold text-sm transition"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
        </span>
        <MessageCircle className="w-4 h-4" />
        <span className="hidden sm:inline">Talk to a program advisor</span>
        <span className="sm:hidden">Talk to us</span>
      </button>
    </div>
  );
};

// ============================================================================
// CohortCountdown — 4-column live D · H · M · S timer.
// Auto-hides when the target date is in the past.
// ============================================================================
const _pad = (n) => String(n).padStart(2, '0');
const _diff = (target) => {
  const now = new Date();
  const ms = Math.max(0, target.getTime() - now.getTime());
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  return { ms, days, hours, mins, secs };
};

export const CohortCountdown = ({
  targetIso,               // ISO date string, e.g. '2026-04-15T09:00:00+05:30'
  label = 'Next cohort begins in',
  className = '',
}) => {
  const target = new Date(targetIso);
  const [t, setT] = useState(() => _diff(target));

  useEffect(() => {
    const id = setInterval(() => setT(_diff(target)), 1000);
    return () => clearInterval(id);
  }, [targetIso, target]);

  if (t.ms <= 0) return null;

  const cells = [
    { k: 'days',  label: 'Days',    val: t.days },
    { k: 'hours', label: 'Hours',   val: _pad(t.hours) },
    { k: 'mins',  label: 'Minutes', val: _pad(t.mins) },
    { k: 'secs',  label: 'Seconds', val: _pad(t.secs) },
  ];

  return (
    <div className={`rounded-2xl border border-amber-300/25 bg-slate-950/60 backdrop-blur px-5 py-4 ${className}`} data-testid="cohort-countdown">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="w-3.5 h-3.5 text-amber-300" />
        <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300">{label.toUpperCase()}</div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {cells.map((c) => (
          <div key={c.k} className="text-center">
            <div className="font-serif text-3xl sm:text-4xl font-bold text-white tabular-nums leading-none" data-testid={`countdown-${c.k}`}>
              {c.val}
            </div>
            <div className="text-[9px] tracking-wider uppercase text-white/50 mt-1">{c.label}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1 mt-3 text-[10px] text-white/45">
        <Clock className="w-3 h-3" />
        <span>{target.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
      </div>
    </div>
  );
};
